<script setup lang="ts">
import { formatMoneyWithSymbol } from '@/lib/money'
import qs from 'qs'
import { catalogApi } from '@/api/client'
import { onBeforeUnmount, Ref, ref, watch } from 'vue'
import { useUserStore } from '@/stores/UserStore'
import { CatalogFilters, CatalogItem, CatalogStats } from '@/types'
import { toast } from 'vue-sonner'
import { debounce } from 'lodash'
import Battery from './Battery.vue'
import BrandLogo from './BrandLogo.vue'
import IntegrationTypeIcon from './IntegrationTypeIcon.vue'
import Grade from './Grade.vue'
import SideBadge from './SideBadge.vue'
import CatalogUpload from './CatalogUpload.vue'
import DropdownFilter from './DropdownFilter.vue'
import FilterPanel from './FilterPanel.vue'
import SortDropdown from './SortDropdown.vue'
import LoadMoreFooter from './LoadMoreFooter.vue'
import { useI18n } from 'vue-i18n'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Upload, Download, Bell } from 'lucide-vue-next'
import { Channel } from 'pusher-js'
import Broadcaster from '@/pusher'
import { useFilterState } from '@/composables/useFilterState'
import { usePagination } from '@/composables/usePagination'

const uploadSheetOpen = ref(false)
const lastUploadDate = ref<string | null>(null)

watch(uploadSheetOpen, async (open) => {
    if (!open) return

    try {
        const { data } = await catalogApi.uploadInfo()
        if (data.uploaded_at) {
            lastUploadDate.value = new Date(data.uploaded_at * 1000).toLocaleString()
        }
    } catch {
        lastUploadDate.value = null
    }
})

const { t } = useI18n({ useScope: 'global' })

type CatalogResponse = {
    items: Array<CatalogItem>
    stats: CatalogStats
    filters: CatalogFilters
    items_count: number
}

let notificationChannel: Channel | null = null

const subscribe = () => {
    if (notificationChannel !== null) {
        return
    }

    console.log('Subscribing...')
    notificationChannel = Broadcaster.get().connector.subscribe(`private-user.${user.profile?.id}.notifications`)
}

const unsubscribe = () => {
    console.log('Unsubscribing...')
    Broadcaster.get().connector.unsubscribe(`private-user.${user.profile?.id}.notifications`)
    notificationChannel?.unbind('catalog-upload-completed')
    notificationChannel = null
}

const user = useUserStore()

const loading = ref(true)
const loadingMore = ref(false)
const list: Ref<Array<CatalogItem>> = ref([])
const stats: Ref<CatalogStats | null> = ref(null)
const filters: Ref<CatalogFilters | null> = ref(null)
const itemsCount: Ref<number> = ref(0)

const processingCatalog = ref(false)

const {
    activeCategoriesFilter,
    activeBrandsFilters,
    activeModelsFilters,
    activeGradesFilters,
    activeStorageFilters,
    activeSearchFilter,
    selectedCriteria,
    filtersCount,
    hasActiveFilters,
    hasData,
    resetFilters: resetFilterState,
} = useFilterState({ items: list, defaultSort: 'grade:desc' })

const { page, nextPage, resetPage } = usePagination()

const syncBasePrice = async (id: number) => {
    try {
        const syncBasePriceResponse = await catalogApi.syncBasePrice(id)
        const catalogItem = syncBasePriceResponse.data as CatalogItem

        const index = list.value.findIndex((item) => item.id === id)

        if (index === -1) {
            return
        }

        list.value[index] = catalogItem

        toast.success('Base price updated')
    } catch (_error) {
        toast.error(t('common.errors.somethingWrong'))
    }
}

const sortingCriteria = [
    { label: 'Highest grade', value: 'grade:desc' },
    { label: 'Lowest grade', value: 'grade:asc' },
    { label: 'Highest price', value: 'price:desc' },
    { label: 'Lowest price', value: 'price:asc' },
    { label: 'Highest stock', value: 'stock:desc' },
    { label: 'Lowest stock', value: 'stock:asc' },
    { label: 'Highest margin', value: 'margin:desc' },
    { label: 'Lowest margin', value: 'margin:asc' },
]

const onSortingChange = async () => {
    resetPage()
    await loadCatalog()
}

const getQuery = () => {
    const query: {
        categories?: string[]
        brands?: string[]
        models?: string[]
        grades?: string[]
        storage?: string[]
        search?: string
        page?: number
        sort?: string
    } = {}

    query.page = page.value
    query.sort = selectedCriteria.value

    // Dropdowns
    if (activeCategoriesFilter.value.length) {
        query.categories = activeCategoriesFilter.value
    }
    if (activeBrandsFilters.value.length) {
        query.brands = activeBrandsFilters.value
    }
    if (activeModelsFilters.value.length) {
        query.models = activeModelsFilters.value
    }
    if (activeGradesFilters.value.length) {
        query.grades = activeGradesFilters.value
    }
    if (activeStorageFilters.value.length) {
        query.storage = activeStorageFilters.value
    }

    // Text search
    if (activeSearchFilter.value) {
        query.search = activeSearchFilter.value
    }

    return query
}

const loadCatalog = async () => {
    try {
        loading.value = true
        const queryString = qs.stringify(getQuery())
        const catalogResponse = (await catalogApi.list(queryString)) as { data: CatalogResponse }

        list.value = catalogResponse.data.items
        stats.value = catalogResponse.data.stats
        filters.value = catalogResponse.data.filters
        itemsCount.value = catalogResponse.data.items_count
    } catch (error) {
        console.error(error)
    } finally {
        loading.value = false
    }
}

const loadMore = async () => {
    await loadMoreItems()
}

const loadMoreItems = async () => {
    try {
        loadingMore.value = true
        nextPage()

        const queryString = qs.stringify(getQuery())
        const itemsResponse = await catalogApi.list(queryString)

        list.value = list.value.concat(itemsResponse.data.items)
    } catch (error) {
        console.error(error)
    } finally {
        loadingMore.value = false
    }
}

const onCategoryFilterChange = async (_event: Event) => {
    resetPage()
    activeBrandsFilters.value = []
    activeModelsFilters.value = []

    await loadCatalog()
}

const onBrandFilterChange = async (_event: Event) => {
    resetPage()
    activeModelsFilters.value = []

    await loadCatalog()
}

const onModelsFilterChange = async () => {
    resetPage()
    await loadCatalog()
}

const onGradesFilterChange = async () => {
    resetPage()
    await loadCatalog()
}

const onStorageFilterChange = async () => {
    resetPage()
    await loadCatalog()
}


const onSearchInputChange = debounce(onModelsFilterChange, 500)

const resetFilters = () => {
    resetFilterState()
    resetPage()
}

const onResetFiltersBtnClick = async () => {
    resetFilters()
    await loadCatalog()
}

const onProcessingCatalog = () => {
    console.log('onProcessingCatalog...')
    processingCatalog.value = true

    notificationChannel?.bind('catalog-upload-completed', (data: { filename: string }) => {
        console.log('Event received', data)
        processingCatalog.value = false
        updateCatalog()
    })
}

const updateCatalog = async () => {
    resetFilters()
    await loadCatalog()
}

const onProcessingError = () => {
    console.log('onProcessingError')
    processingCatalog.value = false
}

const onProcessingSuccess = () => {
    console.log('onProcessingSuccess')
}

const downloadCatalog = async () => {
    window.location.href = `/api/catalog/export`
}

onBeforeUnmount(() => unsubscribe())

subscribe()
loadCatalog()
</script>

<template>
    <div class="space-y-4">
        <div class="-mx-6 sm:-mx-8 border-b px-6 sm:px-8 pb-4">
            <div class="flex flex-wrap items-center justify-between gap-2">
                <h1 class="text-3xl font-bold tracking-tight flex items-center gap-2">
                    {{ t('catalog.title') }}
                    <IntegrationTypeIcon :type="user.configuration?.integration_type || null" />
                </h1>

                <div class="flex flex-wrap items-center gap-3">
                    <Badge
                        v-if="
                            user.configuration?.repricing_mode === 'dry_run' &&
                            user.configuration?.repricing_enabled
                        "
                        variant="warning"
                    >
                        dry run
                    </Badge>
                </div>
            </div>

            <div class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <div
                    v-if="user.configuration?.google_feed_url !== null"
                    class="flex items-start gap-1.5 font-bold"
                >
                    Feed
                    <code class="text-xs font-normal">{{ user.configuration?.google_feed_url }}</code>
                </div>

            </div>
        </div>

    </div>

    <div class="mt-8">
        <!-- Filters & Stats -->
        <div class="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div v-if="hasData">
                <div class="text-sm font-medium">
                    {{ t('common.products') }}: <span class="font-bold">{{ stats?.products }}</span>
                </div>
                <div v-if="stats?.last_update" class="text-xs text-muted-foreground">
                    {{ t('common.updatedAt', { updatedAt: stats.last_update }) }}
                </div>
            </div>

            <div class="flex flex-row gap-2 ml-auto">
                <template v-if="hasData">
                    <Input
                        v-model="activeSearchFilter"
                        type="text"
                        class="h-10 w-48"
                        :placeholder="t('common.filters.search')"
                        @update:model-value="onSearchInputChange"
                    />

                    <FilterPanel
                        :filters-count="filtersCount"
                        :show="true"
                        @reset="onResetFiltersBtnClick"
                    >
                        <DropdownFilter
                            v-model:value="activeCategoriesFilter"
                            :options="filters?.categories || []"
                            :show="!!filters?.categories.length"
                            :button-label="t('common.filters.byCategory')"
                            :options-label="t('common.category')"
                            @update:value="onCategoryFilterChange"
                        />

                        <DropdownFilter
                            v-model:value="activeBrandsFilters"
                            :options="filters?.brands || []"
                            :show="!!filters?.brands.length"
                            :button-label="t('common.filters.byBrand')"
                            options-label="Brands"
                            @update:value="onBrandFilterChange"
                        />

                        <DropdownFilter
                            v-model:value="activeModelsFilters"
                            :options="filters?.models || []"
                            :show="!!filters?.models.length"
                            :button-label="t('common.filters.byModels')"
                            :options-label="t('common.models')"
                            @update:value="onModelsFilterChange"
                        />

                        <DropdownFilter
                            v-model:value="activeGradesFilters"
                            :options="filters?.grades || []"
                            :show="!!filters?.grades.length"
                            :button-label="t('common.filters.byGrade')"
                            :options-label="t('common.grades')"
                            @update:value="onGradesFilterChange"
                        />

                        <DropdownFilter
                            v-model:value="activeStorageFilters"
                            :options="filters?.storage || []"
                            :show="!!filters?.storage.length"
                            :button-label="t('common.filters.byStorage')"
                            :options-label="t('common.storage')"
                            @update:value="onStorageFilterChange"
                        />
                    </FilterPanel>

                    <SortDropdown
                        v-model="selectedCriteria"
                        :criteria="sortingCriteria"
                        :show="true"
                        @change="onSortingChange"
                    />

                    <Button
                        v-if="user.configuration?.integration_type === 's3'"
                        variant="secondary"
                        @click.prevent="downloadCatalog"
                    >
                        Download catalog
                        <Download class="size-4 ml-2" />
                    </Button>
                </template>

                <Sheet v-if="user.configuration?.manual_feed_upload_enabled" v-model:open="uploadSheetOpen">
                    <SheetTrigger as-child>
                        <Button variant="secondary">
                            Upload catalog
                            <Upload class="size-4 ml-2" />
                        </Button>
                    </SheetTrigger>
                    <SheetContent class="flex flex-col">
                        <SheetHeader>
                            <SheetTitle>Upload catalog</SheetTitle>
                            <SheetDescription>
                                Update your catalog by uploading an up-to-date file in the predefined format.
                            </SheetDescription>
                        </SheetHeader>
                        <div class="mt-6">
                            <CatalogUpload
                                :processing="processingCatalog"
                                @processing="onProcessingCatalog"
                                @error="onProcessingError"
                                @success="onProcessingSuccess"
                            />
                        </div>
                        <div class="mt-auto text-center space-y-1">
                            <a
                                href="/api/catalog/upload/download"
                                class="text-xs text-muted-foreground underline"
                            >
                                Download last upload
                            </a>
                            <p v-if="lastUploadDate" class="text-xs text-muted-foreground">
                                Uploaded {{ lastUploadDate }}
                            </p>
                        </div>
                    </SheetContent>
                </Sheet>
            </div>
        </div>

        <!-- Table: one tooltip provider for the whole list, not one per row -->
        <TooltipProvider>
            <!-- Header -->
            <div v-if="!loading && list.length > 0" class="catalog-row border-b py-3 text-xs font-medium text-muted-foreground uppercase">
                <div class="catalog-col-brand"></div>
                <div class="catalog-col-product">{{ t('catalog.table.headings.product') }}</div>
                <div class="catalog-col-price">{{ t('catalog.table.headings.activePrice') }}</div>
                <div class="catalog-col-base">{{ t('catalog.table.headings.basePrice') }}</div>
                <div class="catalog-col-dynamic">{{ t('catalog.table.headings.dynamicPrice') }}</div>
            </div>

            <!-- Empty state: no data at all -->
            <div v-if="!loading && list.length === 0 && !hasActiveFilters" class="py-12 text-center text-sm text-muted-foreground">
                {{ t('catalog.empty') }}
            </div>

            <!-- No results: filters active but nothing matches -->
            <div v-else-if="!loading && list.length === 0" class="py-12 text-center text-sm text-muted-foreground">
                {{ t('common.noResults') }}
            </div>

            <!-- Rows -->
            <div
                v-for="item in list"
                v-show="!loading"
                :key="item.id"
                class="catalog-row border-b py-3 transition-colors hover:bg-muted/50"
            >
                <!-- Brand -->
                <div class="catalog-col-brand">
                    <BrandLogo :brand="item.brand_name" colored />
                </div>

                <!-- Product (two lines: name + metadata) -->
                <div class="catalog-col-product min-w-0">
                    <div class="flex items-center gap-2 font-medium">
                        <router-link
                            :to="{ name: 'dashboard.catalog.detail', params: { id: item.id } }"
                            class="underline hover:text-muted-foreground text-sm 2xl:text-base"
                        >
                            {{ item.name }}
                        </router-link>

                        <Bell
                            v-if="item.notifications_count !== undefined && item.notifications_count > 0"
                            class="size-4 text-yellow-400 shrink-0"
                        />

                        <!-- What this row's own price is: a sell price or an offer to pay. Absent when it has none. -->
                        <SideBadge v-if="item.price_side" :side="item.price_side" />
                    </div>

                    <!-- Separators come from CSS, so a field is added or dropped without touching its neighbours -->
                    <div class="catalog-meta flex flex-wrap items-center gap-x-3 mt-1 text-xs text-muted-foreground">
                        <Grade :grade="item.grade" plain />
                        <span v-if="item.external_id">ID: {{ item.external_id }}</span>
                        <span v-if="item.ean">EAN: {{ item.ean }}</span>
                        <span v-if="item.storage">{{ item.storage }}</span>
                        <span v-if="item.ram">RAM: {{ item.ram }}</span>
                        <span v-if="item.color" class="capitalize">{{ item.color }}</span>
                        <Battery v-if="item.battery_type" :status="item.battery_type" class="text-muted-foreground" />
                        <span v-if="item.margin_percentage">Margin: {{ item.margin_percentage }}%</span>
                        <span v-if="item.stock">Stock: {{ item.stock }}</span>
                    </div>
                </div>

                <!-- Price -->
                <div class="catalog-col-price font-semibold tabular-nums">
                    <span v-if="item.price">{{ formatMoneyWithSymbol(item.price, item.currency_symbol) }}</span>
                </div>

                <!-- Base Price -->
                <div class="catalog-col-base flex items-center gap-2 tabular-nums">
                    <span v-if="item.base_price">{{ formatMoneyWithSymbol(item.base_price, item.currency_symbol) }}</span>

                    <!-- Syncing a base price needs one unambiguous currency to push -->
                    <Button
                        v-if="item.currency && item.price && item.base_price && item.price < item.base_price"
                        variant="outline"
                        size="xs"
                        @click.prevent="syncBasePrice(item.id)"
                    >
                        Sync
                    </Button>
                </div>

                <!-- Dynamic: state carried by a dot, with the wording in the tooltip -->
                <div class="catalog-col-dynamic">
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <span
                                class="block size-2 rounded-full"
                                :class="item.automatic_price_enabled ? 'bg-green-500' : 'border border-muted-foreground/40'"
                            />
                        </TooltipTrigger>
                        <TooltipContent side="left">
                            Dynamic pricing is {{ item.automatic_price_enabled ? 'active' : 'inactive' }} for this product
                        </TooltipContent>
                    </Tooltip>
                </div>
            </div>

            <!-- Skeleton rows: a full page while loading, a short run while appending -->
            <template v-if="loading || loadingMore">
                <div
                    v-for="n in loading ? 8 : 4"
                    :key="`skeleton-${n}`"
                    class="catalog-row border-b py-3"
                >
                    <div class="catalog-col-brand">
                        <Skeleton class="size-4 rounded" />
                    </div>
                    <div class="catalog-col-product space-y-2">
                        <Skeleton class="h-4 w-3/4" />
                        <Skeleton class="h-3 w-1/2" />
                    </div>
                    <div class="catalog-col-price">
                        <Skeleton class="h-4 w-16" />
                    </div>
                    <div class="catalog-col-base">
                        <Skeleton class="h-4 w-16" />
                    </div>
                    <div class="catalog-col-dynamic">
                        <Skeleton class="size-2 rounded-full" />
                    </div>
                </div>
            </template>
        </TooltipProvider>

        <LoadMoreFooter v-if="list.length > 0" :current-count="list.length" :total-count="itemsCount" @load-more="loadMore" />
    </div>

</template>

<style scoped>
.catalog-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
}

.catalog-meta > *:not(:last-child)::after {
    content: '·';
    margin-left: 0.75rem;
}

.catalog-col-brand {
    width: 1rem;
    flex-shrink: 0;
}

.catalog-col-product {
    flex: 1 1 0%;
    min-width: 0;
    max-width: 48rem;
}

.catalog-col-price {
    width: 6rem;
    flex-shrink: 0;
}

.catalog-col-base {
    width: 8rem;
    flex-shrink: 0;
}

.catalog-col-dynamic {
    width: 2.5rem;
    flex-shrink: 0;
}

@media (min-width: 1536px) {
    .catalog-row {
        gap: 1.5rem;
    }

    .catalog-col-price {
        width: 7rem;
    }

    .catalog-col-base {
        width: 9rem;
    }
}
</style>
